import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isAndroid } from '../utils/printService';

const VKB_QWERTY = [
  ['1','2','3','4','5','6','7','8','9','0','⌫'],
  ['a','z','e','r','t','y','u','i','o','p'],
  ['q','s','d','f','g','h','j','k','l','m'],
  ['⇧','w','x','c','v','b','n',',','.','↵'],
];
const VKB_NUM = [['7','8','9'],['4','5','6'],['1','2','3'],['.','0','⌫']];
const VKB_STORAGE_KEY = 'ef_vkb_state';

export function isLargeAndroidScreen() {
  if (!isAndroid()) return false;
  // Diagonale CSS px / 96 dpi ≥ 14 pouces
  return Math.sqrt(window.screen.width ** 2 + window.screen.height ** 2) >= 1344;
}

export default function VirtualKeyboard({ onClose }) {
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(VKB_STORAGE_KEY)) || {}; } catch { return {}; }
  }, []);

  const [mode,  setMode]  = useState(saved.mode  || 'num');
  const [shift, setShift] = useState(false);
  const [scale, setScale] = useState(saved.scale || 1);
  const [pos,   setPos]   = useState(() => saved.pos || {
    x: Math.max(8, Math.round(window.innerWidth / 2 - 120)),
    y: Math.max(8, window.innerHeight - 340),
  });

  const kbRef     = useRef(null);
  const dragging  = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const persist = useCallback((p, s, m) => {
    try { localStorage.setItem(VKB_STORAGE_KEY, JSON.stringify({ pos: p, scale: s, mode: m })); } catch {}
  }, []);

  // ── Drag ──────────────────────────────────────────────────────────────
  const onDragStart = useCallback((e) => {
    if (e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    dragging.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragStart.current = { mx: cx, my: cy, px: pos.x, py: pos.y };
  }, [pos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      if (e.cancelable) e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const kw = (kbRef.current?.offsetWidth  || 308) * scale;
      const kh = (kbRef.current?.offsetHeight || 300) * scale;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth  - kw, dragStart.current.px + (cx - dragStart.current.mx))),
        y: Math.max(0, Math.min(window.innerHeight - kh, dragStart.current.py + (cy - dragStart.current.my))),
      });
    };
    const onEnd = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setPos(p => { persist(p, scale, mode); return p; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onEnd);
    };
  }, [scale, mode, persist]);

  // ── Injection ─────────────────────────────────────────────────────────
  const nativeInputSetter    = useMemo(() => Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype,    'value')?.set, []);
  const nativeTextareaSetter = useMemo(() => Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set, []);

  const getNativeSetter = useCallback((el) =>
    el.tagName === 'TEXTAREA' ? nativeTextareaSetter : nativeInputSetter,
  [nativeInputSetter, nativeTextareaSetter]);

  const inject = useCallback((char) => {
    const el = document.activeElement;
    if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
    const s = el.selectionStart ?? el.value.length;
    const e = el.selectionEnd   ?? el.value.length;
    const nv = el.value.slice(0, s) + char + el.value.slice(e);
    try { getNativeSetter(el)?.call(el, nv); el.dispatchEvent(new Event('input', { bubbles: true })); } catch {}
    requestAnimationFrame(() => { try { el.setSelectionRange(s + char.length, s + char.length); } catch {} });
  }, [getNativeSetter]);

  const backspace = useCallback(() => {
    const el = document.activeElement;
    if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const nv = s !== e
      ? el.value.slice(0, s) + el.value.slice(e)
      : s > 0 ? el.value.slice(0, s - 1) + el.value.slice(s) : null;
    if (nv === null) return;
    try { getNativeSetter(el)?.call(el, nv); el.dispatchEvent(new Event('input', { bubbles: true })); } catch {}
    const np = s !== e ? s : Math.max(0, s - 1);
    requestAnimationFrame(() => { try { el.setSelectionRange(np, np); } catch {} });
  }, [getNativeSetter]);

  const handleKey = useCallback((k) => {
    if (k === '⌫') { backspace(); return; }
    if (k === '⇧') { setShift(v => !v); return; }
    if (k === '↵') {
      const el = document.activeElement;
      if (el?.tagName === 'TEXTAREA') { inject('\n'); return; }
      if (el) el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
      return;
    }
    if (k === '__space__') { inject(' '); return; }
    const ch = shift && /^[a-z]$/.test(k) ? k.toUpperCase() : k;
    inject(ch);
    if (shift && /^[a-z]$/.test(k)) setShift(false);
  }, [inject, backspace, shift]);

  const changeScale = (s) => { setScale(s); persist(pos,   s, mode); };
  const changeMode  = (m) => { setMode(m);  persist(pos, scale, m);  };

  // ── Bouton touche ─────────────────────────────────────────────────────
  const kbtn = (k, label, extra = '') => (
    <button key={k + (label || '')}
      onMouseDown={e => e.preventDefault()}
      onTouchStart={e => e.preventDefault()}
      onClick={() => handleKey(k)}
      className={`flex items-center justify-center rounded-lg text-sm font-bold transition-all active:scale-90 h-10 select-none
        ${extra}
        ${k === '⌫' ? 'bg-orange-200 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 px-2' :
          k === '↵' ? 'bg-indigo-600 text-white px-2' :
          k === '⇧' ? (shift ? 'bg-indigo-300 dark:bg-indigo-700 text-indigo-800 dark:text-indigo-100 px-2'
                              : 'bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-white px-2') :
          'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white w-9'}`}>
      {label ?? (shift && /^[a-z]$/.test(k) ? k.toUpperCase() : k)}
    </button>
  );

  return (
    <div
      ref={kbRef}
      style={{ position: 'fixed', left: pos.x, top: pos.y, transform: `scale(${scale})`, transformOrigin: 'top left', zIndex: 9999, width: 308 }}
      className="shadow-2xl rounded-2xl overflow-hidden select-none"
      onMouseDown={e => e.preventDefault()}
      onTouchStart={e => { if (e.target.tagName !== 'BUTTON') e.preventDefault(); }}
    >
      {/* ── Barre titre / poignée ── */}
      <div
        className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-700 dark:bg-slate-950 text-white cursor-grab active:cursor-grabbing touch-none"
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        <span className="text-slate-400 text-base leading-none pointer-events-none">⠿</span>

        <div className="flex gap-1 flex-1">
          {[['num','123'],['qwerty','ABC']].map(([m, lbl]) => (
            <button key={m}
              onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
              onClick={() => changeMode(m)}
              className={`px-2.5 py-0.5 rounded text-xs font-bold transition ${mode === m ? 'bg-indigo-500 text-white' : 'bg-slate-500 text-slate-200 hover:bg-slate-400'}`}>
              {lbl}
            </button>
          ))}
        </div>

        <div className="flex gap-0.5">
          {[[0.75,'S'],[1,'M'],[1.3,'L']].map(([s, lbl]) => (
            <button key={s}
              onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
              onClick={() => changeScale(s)}
              className={`w-6 h-6 rounded text-xs font-bold transition ${scale === s ? 'bg-indigo-400 text-white' : 'bg-slate-500 text-slate-300 hover:bg-slate-400'}`}>
              {lbl}
            </button>
          ))}
        </div>

        <button
          onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
          onClick={onClose}
          className="w-6 h-6 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-bold flex items-center justify-center transition leading-none">
          ×
        </button>
      </div>

      {/* ── Pavé numérique ── */}
      {mode === 'num' && (
        <div className="bg-slate-200 dark:bg-slate-800 p-2">
          <div className="grid grid-cols-3 gap-1.5">
            {VKB_NUM.flat().map((k, i) => (
              <button key={i}
                onMouseDown={e => e.preventDefault()} onTouchStart={e => e.preventDefault()}
                onClick={() => handleKey(k)}
                className={`h-12 rounded-xl text-xl font-bold transition-all active:scale-90 select-none
                  ${k === '⌫'
                    ? 'bg-orange-200 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                    : 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white'}`}>
                {k}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Clavier AZERTY ── */}
      {mode === 'qwerty' && (
        <div className="bg-slate-200 dark:bg-slate-800 px-1.5 py-1.5 space-y-1">
          {VKB_QWERTY.map((row, ri) => (
            <div key={ri} className="flex gap-0.5 justify-center">
              {row.map(k => kbtn(k))}
            </div>
          ))}
          <div className="flex gap-0.5 px-1">
            {kbtn('__space__', 'espace', 'flex-1 bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-white')}
          </div>
        </div>
      )}
    </div>
  );
}
